import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Mail, Eye, Copy, Check, Facebook, Instagram } from "lucide-react";
import { Section, SectionTitle, Reveal } from "../components/UIBits";
import { SOCIAL } from "../lib/constants";
import { useT } from "../lib/i18n";

const CONTACT_EMAIL = "defiultimedek@gmail.com";

export default function ContactPage() {
    const { t } = useT();
    const [showEmail, setShowEmail] = useState(false);
    const [copied, setCopied] = useState(false);

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(CONTACT_EMAIL);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            const el = document.createElement("textarea");
            el.value = CONTACT_EMAIL;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div data-testid="contact-page">
            <Helmet><title>{`${t("contact.title")} — Défi Ultime`}</title></Helmet>
            <Section>
                <SectionTitle kicker={t("contact.kicker")} title={t("contact.title")} />
                <div className="max-w-2xl">
                    <Reveal>
                        <div className="glass-card p-8 border-l-4 border-[#7FD93C]" data-testid="contact-email-block">
                            <Mail className="text-[#7FD93C] mb-4" size={32} />
                            <h3 className="font-display text-3xl uppercase tracking-widest mb-3">{t("contact.email_heading")}</h3>
                            <p className="text-sm text-white/70 mb-6">{t("contact.email_body")}</p>

                            {!showEmail ? (
                                <button
                                    data-testid="btn-reveal-email"
                                    onClick={() => setShowEmail(true)}
                                    className="btn-outline inline-flex items-center gap-2"
                                >
                                    <Eye size={16} /> {t("contact.see_email")}
                                </button>
                            ) : (
                                <div className="grid gap-4">
                                    <button
                                        type="button"
                                        onClick={copyEmail}
                                        className="w-full text-left flex items-center gap-3 bg-black/40 hover:bg-black/60 active:bg-[#7FD93C]/15 p-4 transition-colors group cursor-pointer border-l-2 border-transparent hover:border-[#7FD93C]"
                                        data-testid="btn-copy-contact-email"
                                    >
                                        <Mail size={20} className="text-[#7FD93C] flex-shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <div className="label-mini mb-1">{t("common.tap_to_copy")}</div>
                                            <div className="font-mono text-base break-all">{CONTACT_EMAIL}</div>
                                        </div>
                                        <span className={`flex-shrink-0 px-2.5 py-1 angle-tag border font-display text-[11px] tracking-widest uppercase flex items-center gap-1 transition-colors ${copied ? "bg-[#7FD93C] border-[#7FD93C] text-black" : "bg-[#7FD93C]/15 border-[#7FD93C]/40 text-[#7FD93C] group-hover:bg-[#7FD93C] group-hover:text-black"}`}>
                                            {copied ? <><Check size={12} /> {t("common.copied")}</> : <><Copy size={12} /> {t("common.copy")}</>}
                                        </span>
                                    </button>
                                    <a
                                        href={`mailto:${CONTACT_EMAIL}`}
                                        data-testid="link-mailto"
                                        className="btn-primary justify-center"
                                    >
                                        <Mail size={16} /> {t("contact.open_mail_app")}
                                    </a>
                                </div>
                            )}
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div className="glass-card p-6 mt-6">
                            <div className="label-mini mb-3">{t("contact.follow")}</div>
                            <div className="flex gap-3">
                                <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" className="p-3 border border-white/10 hover:border-[#7FD93C] hover:text-[#7FD93C] transition-colors" aria-label="Facebook" data-testid="link-contact-facebook"><Facebook size={20} /></a>
                                <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" className="p-3 border border-white/10 hover:border-[#7FD93C] hover:text-[#7FD93C] transition-colors" aria-label="Instagram" data-testid="link-contact-instagram"><Instagram size={20} /></a>
                            </div>
                            <p className="text-xs text-white/40 mt-4">{t("contact.follow_footer")}</p>
                        </div>
                    </Reveal>
                </div>
            </Section>
        </div>
    );
}
